import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

const money = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

export default function Orders() {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({ status: '', search: '', showArchived: false });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);

  const loadOrders = async () => {
    try {
      setError('');
      setLoading(true);

      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);

      const data = await api.get(`/orders?${params.toString()}`, token);
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const visibleOrders = useMemo(() => {
    if (filters.showArchived) {
      return orders;
    }

    return orders.filter(
      (order) => order.status !== 'archived' && order.status !== 'cancelled'
    );
  }, [orders, filters.showArchived]);

  const updateStatus = async (orderId, status, successMessage) => {
    try {
      setSavingId(orderId);
      setError('');
      setSuccess('');

      await api.patch(`/orders/${orderId}/status`, { status }, token);
      setSuccess(successMessage || 'Order updated');
      await loadOrders();
    } catch (err) {
      setError(err.message || 'Failed to update order');
    } finally {
      setSavingId(null);
      setConfirmModal(null);
    }
  };

  const requestArchiveOrder = (order) => {
    setConfirmModal({
      title: 'Archive this order?',
      message: `${order.order_number} will be hidden from the default orders list. The order record, payment details, and items are preserved.`,
      confirmLabel: 'Archive order',
      destructive: false,
      onConfirm: () =>
        updateStatus(
          order.id,
          'archived',
          `${order.order_number} archived successfully.`
        )
    });
  };

  useEffect(() => {
    loadOrders();
  }, [token, filters.status]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
        <p className="mt-1 text-sm text-slate-500">Fulfillment pipeline and archived order management.</p>
      </div>

      <div className="card p-4 md:flex md:items-center md:justify-between">
        <div className="flex flex-col gap-3 md:flex-row">
          <input
            placeholder="Search order, name, or email"
            className="input-field"
            value={filters.search}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') loadOrders();
            }}
          />

          <select
            className="select-field"
            value={filters.status}
            onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="processing">Processing</option>
            <option value="packed">Packed</option>
            <option value="shipped">Shipped</option>
            <option value="completed">Completed</option>
            <option value="delivered">Delivered</option>
            <option value="archived">Archived</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <label className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-2 text-sm">
            <input
              type="checkbox"
              checked={filters.showArchived}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, showArchived: e.target.checked }))
              }
            />
            Show archived/cancelled
          </label>
        </div>

        <button onClick={loadOrders} className="btn-primary mt-3 md:mt-0">
          Refresh
        </button>
      </div>

      {error ? <div className="status-banner-error">{error}</div> : null}
      {success ? <div className="status-banner-success">{success}</div> : null}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Customer</th>
              <th>Status</th>
              <th>Total</th>
              <th>Items</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {visibleOrders.map((order) => (
              <tr key={order.id}>
                <td>
                  <Link className="font-semibold text-orange-700" to={`/orders/${order.id}`}>
                    {order.order_number}
                  </Link>
                </td>

                <td>
                  {order.shipping_name ||
                    `${order.customer_first_name || ''} ${order.customer_last_name || ''}`.trim() ||
                    order.customer_email}
                </td>

                <td>
                  <select
                    className="rounded-lg border border-slate-200 px-2 py-2 min-h-[44px]"
                    value={order.status || 'new'}
                    disabled={savingId === order.id}
                    onChange={(e) => updateStatus(order.id, e.target.value, 'Order status updated')}
                  >
                    <option value="new">New</option>
                    <option value="processing">Processing</option>
                    <option value="packed">Packed</option>
                    <option value="shipped">Shipped</option>
                    <option value="completed">Completed</option>
                    <option value="delivered">Delivered</option>
                    <option value="archived">Archived</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </td>

                <td>{money(order.total)}</td>
                <td>{order.item_count}</td>
                <td>{new Date(order.created_at).toLocaleString()}</td>

                <td>
                  <div className="flex flex-wrap gap-2">
                    <Link className="btn-primary px-3 py-2 text-xs" to={`/orders/${order.id}`}>
                      View
                    </Link>

                    <button
                      onClick={() => updateStatus(order.id, 'processing', 'Order marked processing')}
                      disabled={savingId === order.id}
                      className="btn-secondary px-3 py-2 text-xs"
                    >
                      Process
                    </button>

                    <button
                      onClick={() => updateStatus(order.id, 'shipped', 'Order marked shipped')}
                      disabled={savingId === order.id}
                      className="btn-accent px-3 py-2 text-xs"
                    >
                      Shipped
                    </button>

                    {order.status !== 'archived' ? (
                      <button
                        onClick={() => requestArchiveOrder(order)}
                        disabled={savingId === order.id}
                        className="btn-ghost px-3 py-2 text-xs"
                      >
                        Archive
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}

            {!visibleOrders.length && !loading ? (
              <tr>
                <td colSpan="7">No orders found.</td>
              </tr>
            ) : null}

            {loading ? (
              <tr>
                <td colSpan="7">Loading orders...</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        confirmLabel={confirmModal?.confirmLabel || 'Confirm'}
        destructive={confirmModal?.destructive}
        loading={Boolean(savingId)}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />
    </div>
  );
}
